import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { usePageTitle } from '@/hooks/usePageTitle';
import { 
  Mail, 
  Phone, 
  MapPin, 
  Send, 
  Building2,
  User,
  MessageSquare,
  Briefcase,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import axios from 'axios';

const ContactUs = () => {
  // Set SEO-optimized title for Contact page
  usePageTitle('contact');
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    inquiryType: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.6 }
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSelectChange = (value: string) => {
    setFormData({
      ...formData,
      inquiryType: value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');
    setErrorMessage('');

    try {
      const response = await axios.post('/api/contact', formData);
      
      if (response.data.success) {
        setSubmitStatus('success');
        // Reset form
        setFormData({
          name: '',
          email: '',
          company: '',
          inquiryType: '',
          message: ''
        });
      } else {
        throw new Error('Failed to send message');
      }
    } catch (error) {
      setSubmitStatus('error');
      setErrorMessage('Failed to send your message. Please try again or email us directly at support@dineai.ai');
      console.error('Contact form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#1f4ed1]">
      <Header />

      {/* Hero Section - Dark blue background */}
      <section className="relative pt-32 pb-20 bg-[#2256e5] text-white overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-blue-600/20 to-transparent" />
        
        {/* Decorative circles */}
        <div className="absolute top-20 left-10 w-32 h-32 bg-white/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-10 right-10 w-48 h-48 bg-dineai-yellow/10 rounded-full blur-3xl"></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left side - Text content */}
            <motion.div
              initial="hidden"
              animate="visible"
              variants={fadeInUp}
              className="text-left"
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-dineai-yellow/20 backdrop-blur-sm rounded-full mb-6">
                <MessageSquare className="w-4 h-4 text-dineai-yellow" />
                <span className="text-dineai-yellow font-medium text-sm">GET IN TOUCH</span>
              </div>
              
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                Let's Start a{" "}
                <span className="bg-gradient-to-r from-[#FDB913] via-yellow-300 to-[#FFE563] bg-clip-text text-transparent">
                  Conversation
                </span>
              </h1>
              
              <p className="text-xl text-blue-100 mb-8">
                Whether you're a restaurant owner ready to transform your business or an enterprise 
                looking to scale, we're here to help you succeed with AI-powered solutions.
              </p>

              {/* Single Email Contact Option */}
              <motion.div 
                className="inline-flex items-center gap-4 bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20"
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.3 }}
              >
                <div className="w-12 h-12 bg-dineai-yellow rounded-full flex items-center justify-center">
                  <Mail className="h-6 w-6 text-[#1f4ed1]" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-1">Email Us Directly</h3>
                  <a href="mailto:support@dineai.ai" className="text-dineai-yellow hover:underline text-lg">
                    support@dineai.ai
                  </a>
                </div>
              </motion.div>
            </motion.div>

            {/* Right side - Hero Image */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative flex justify-center items-center"
            >
              <img
                src="/contact-us.png"
                alt="Contact Dine AI"
                className="w-auto h-auto max-w-sm lg:max-w-md"
                style={{ filter: 'drop-shadow(0 20px 40px rgba(0, 0, 0, 0.2))' }}
                onError={(e) => {
                  e.currentTarget.src = '/hero-dessert.png';
                }}
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Contact Form Section - Light blue background */}
      <section id="contact-form" className="py-20 bg-[#1f4ed1]">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-start">
              
              {/* Left side - Form */}
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInUp}
                className="bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20 p-10"
              >
                <h2 className="text-3xl font-bold text-white mb-6">
                  Send Us a Message
                </h2>
                
                {submitStatus === 'success' && (
                  <motion.div 
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3"
                  >
                    <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                    <div>
                      <h3 className="font-semibold text-green-900">Message sent successfully!</h3>
                      <p className="text-green-700 text-sm mt-1">
                        We'll get back to you within 24 hours.
                      </p>
                    </div>
                  </motion.div>
                )}

                {submitStatus === 'error' && (
                  <motion.div 
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3"
                  >
                    <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
                    <div>
                      <h3 className="font-semibold text-red-900">Something went wrong</h3>
                      <p className="text-red-700 text-sm mt-1">
                        {errorMessage}
                      </p>
                    </div>
                  </motion.div>
                )}

                <form onSubmit={handleSubmit} className="space-y-8">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="name" className="text-white mb-2 block">
                        Your Name *
                      </Label>
                      <div className="relative">
                        <User className="absolute left-3 top-3 h-5 w-5 text-white/50 z-10" />
                        <Input
                          id="name"
                          name="name"
                          type="text"
                          required
                          value={formData.name}
                          onChange={handleInputChange}
                          className="pl-10 h-12 bg-white/20 border-white/30 text-white placeholder:text-white/50 focus:bg-white/30 focus:border-dineai-yellow focus:ring-2 focus:ring-dineai-yellow/50"
                          placeholder="John Doe"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="email" className="text-white mb-2 block">
                        Email Address *
                      </Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-3 h-5 w-5 text-white/50 z-10" />
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          required
                          value={formData.email}
                          onChange={handleInputChange}
                          className="pl-10 h-12 bg-white/20 border-white/30 text-white placeholder:text-white/50 focus:bg-white/30 focus:border-dineai-yellow focus:ring-2 focus:ring-dineai-yellow/50"
                          placeholder="john@example.com"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="company" className="text-white mb-2 block">
                        Company/Restaurant
                      </Label>
                      <div className="relative">
                        <Building2 className="absolute left-3 top-3 h-5 w-5 text-white/50 z-10" />
                        <Input
                          id="company"
                          name="company"
                          type="text"
                          value={formData.company}
                          onChange={handleInputChange}
                          className="pl-10 h-12 bg-white/20 border-white/30 text-white placeholder:text-white/50 focus:bg-white/30 focus:border-dineai-yellow focus:ring-2 focus:ring-dineai-yellow/50"
                          placeholder="Your Restaurant Name"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="inquiryType" className="text-white mb-2 block">
                        Inquiry Type *
                      </Label>
                      <div className="relative">
                        <Briefcase className="absolute left-3 top-3 h-5 w-5 text-white/50 z-10 pointer-events-none" />
                        <Select value={formData.inquiryType} onValueChange={handleSelectChange}>
                          <SelectTrigger className="pl-10 h-12 bg-white/20 border-white/30 text-white [&>svg]:text-white focus:border-dineai-yellow focus:ring-2 focus:ring-dineai-yellow/50">
                            <SelectValue placeholder="Select inquiry type" className="placeholder:text-white/50" />
                          </SelectTrigger>
                          <SelectContent className="bg-[#2256e5] border-white/30">
                            <SelectItem value="sales" className="text-white hover:bg-white/20">Sales Inquiry</SelectItem>
                            <SelectItem value="support" className="text-white hover:bg-white/20">Technical Support</SelectItem>
                            <SelectItem value="enterprise" className="text-white hover:bg-white/20">Enterprise Solutions</SelectItem>
                            <SelectItem value="partnership" className="text-white hover:bg-white/20">Partnership Opportunities</SelectItem>
                            <SelectItem value="demo" className="text-white hover:bg-white/20">Request a Demo</SelectItem>
                            <SelectItem value="other" className="text-white hover:bg-white/20">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-white mb-2 block">
                      Your Message *
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      required
                      value={formData.message}
                      onChange={handleInputChange}
                      rows={10}
                      className="resize-none bg-white/20 border-white/30 text-white placeholder:text-white/50 focus:bg-white/30 focus:border-dineai-yellow focus:ring-2 focus:ring-dineai-yellow/50"
                      placeholder="Tell us how we can help you..."
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-dineai-yellow text-black hover:bg-dineai-yellow/90 py-6 text-lg font-bold rounded-lg shadow-lg transform hover:scale-105 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <>Sending...</>
                    ) : (
                      <>
                        Send Message
                        <Send className="ml-2 h-5 w-5" />
                      </>
                    )}
                  </Button>
                </form>
              </motion.div>

              {/* Right side - Info Cards */}
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInUp}
                className="lg:sticky lg:top-32"
              >
                <h3 className="text-2xl font-bold text-white mb-6">
                  Why Choose Dine AI?
                </h3>

                {/* Info Cards */}
                <div className="space-y-4">
                  <div className="bg-gradient-to-r from-emerald-500/10 to-green-500/10 rounded-xl p-6 border border-emerald-500/20">
                    <h3 className="font-bold text-white mb-2 flex items-center gap-2">
                      <span className="text-2xl">⚡</span>
                      Fast Response Time
                    </h3>
                    <p className="text-white/70">
                      We typically respond within 24 hours during business days.
                    </p>
                  </div>

                  <div className="bg-gradient-to-r from-blue-500/10 to-cyan-500/10 rounded-xl p-6 border border-blue-500/20">
                    <h3 className="font-bold text-white mb-2 flex items-center gap-2">
                      <span className="text-2xl">🎯</span>
                      Enterprise Solutions
                    </h3>
                    <p className="text-white/70">
                      Special support for chains with 50+ locations. Scalable AI solutions for your growth.
                    </p>
                  </div>

                  <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-xl p-6 border border-purple-500/20">
                    <h3 className="font-bold text-white mb-2 flex items-center gap-2">
                      <span className="text-2xl">🤝</span>
                      Partnership Opportunities
                    </h3>
                    <p className="text-white/70">
                      Join us in revolutionizing the restaurant industry with AI-powered solutions.
                    </p>
                  </div>

                  <div className="bg-gradient-to-r from-yellow-500/10 to-orange-500/10 rounded-xl p-6 border border-yellow-500/20">
                    <h3 className="font-bold text-white mb-2 flex items-center gap-2">
                      <span className="text-2xl">🚀</span>
                      Proven Results
                    </h3>
                    <p className="text-white/70">
                      Join hundreds of restaurants already transforming their business with Dine AI.
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactUs;